import { useEffect, useRef, type ReactNode } from "react";
import { Navigate } from "react-router";
import type { ModuleId } from "@wato/business-types";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { useToast } from "@wato/ui";
import { mayUse } from "../features/roles/role-label";
import { moduleNameKey } from "../features/settings/workspace/module-name";
import { useModules } from "../features/workspace/useModules";
import { useWorkspace } from "../features/workspace/WorkspaceContext";
import { BlockedState } from "./BlockedState";
import { NoAccessState } from "./NoAccessState";

/** OWN-333: the admin hid the module — nothing to explain or switch on, so go home with one toast. */
function HiddenModuleRedirect({ workspaceId }: { readonly workspaceId: string }) {
  const { t } = useI18n();
  const toast = useToast();
  const shown = useRef(false);
  useEffect(() => {
    if (shown.current) return;
    shown.current = true;
    toast.show({ tone: "info", title: t("blocked.b29b.moduleOff.hidden") });
  }, [toast, t]);
  return <Navigate to={`/w/${workspaceId}`} replace />;
}

/**
 * Q4-ONB-08: shared route guard. A member without any of the `anyOf` permissions sees one
 * explanatory empty state instead of a blank page or data the API will refuse anyway (the API
 * stays the real protection, ADR-0017). Same `granted` source the sidebar filters on.
 */
export function RequirePermission({
  anyOf,
  reasonKey = "blocked.noAccess.page",
  module,
  children,
}: {
  readonly anyOf: readonly string[];
  /** A page-specific explanation (defaults to the generic «no permission to view this page»). */
  readonly reasonKey?: MessageKey;
  /** b84/b88: a direct URL of a page whose module is off shows an explanation, not data. Several = any off blocks. */
  readonly module?: ModuleId | readonly ModuleId[];
  readonly children: ReactNode;
}) {
  const { t } = useI18n();
  const { workspace } = useWorkspace();
  const modules = useModules();
  const required = module === undefined ? [] : Array.isArray(module) ? module : [module];
  const off = required.find((m) => !modules.isOn(m));
  if (off) {
    if (!modules.ready) return null;
    if (modules.isHiddenByAdmin(off)) return <HiddenModuleRedirect workspaceId={workspace.id} />;
    const title = t("blocked.b29b.moduleOff.title", { module: t(moduleNameKey(off)) });
    // Only an owner can switch modules, so only an owner gets the Settings → Modules link.
    if (!workspace.isOwner) {
      return <NoAccessState title={title} reason={t("blocked.b29b.moduleOff.ask")} />;
    }
    return (
      <BlockedState
        title={title}
        reason={t("blocked.noAccess.moduleOff")}
        ctaLabel={t("blocked.b29b.moduleOff.cta")}
        ctaTo={`/w/${workspace.id}/settings/workspace/modules`}
      />
    );
  }
  if (!anyOf.some((permission) => mayUse(workspace, permission))) {
    return <NoAccessState reason={t(reasonKey)} />;
  }
  return <>{children}</>;
}
